import React, { useEffect, useState } from "react";

import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
  Grid
} from "@material-ui/core";
import { appUrl } from "../../../../utils/config";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    maxWidth: 750,
    border: "1px solid #e0e0e0",
    marginTop: 10,
  },
  tablefacility: {
    maxWidth: 1000,
    border: "1px solid #e0e0e0",
    marginTop: 10,
  },
  headerCell: {
    padding: "6px 8px",
    fontSize: "0.875rem",
  },
  bodyCell: {
    padding: "4px 8px",
    fontSize: "0.875rem",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  confidenceSection: {
    marginTop: "2rem",
    width: "100%",
    maxWidth: "1250px",
  },
  confidenceHeading: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: theme.spacing(1),
  },
  confidenceValue: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#555",
    paddingTop: theme.spacing(1), 
    paddingBottom: theme.spacing(1),
    borderRadius: 4,
    display: "inline-block",
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  backButton: {
    backgroundColor: "#555",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const SponsorNetworkList = (props) => {
  const classes = useStyles();
  const {
    addSponsorList,
    rowData,
    rowNetwork,
    updateNetworkData, reviewData} = props;
  const dispatch = useDispatch();
  const [modalities, setModalities] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [plantTypes, setPlantTypes] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [poStatuses, setPoStatuses] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchStatuses, setBatchStatuses] = useState([]);
  const [shipmentStatuses, setShipmentStatuses] = useState([]);
  const [formatTypes, setFormatTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [digitalizationCodes, setDigitalizationCodes] = useState([]);
  const [lowConfidenceLevel, setLowConfidenceLevel] = useState("");
  const [mediumConfidenceLevel, setMediumConfidenceLevel] = useState({ min: "", max: "" });
  const [highConfidenceLevel, setHighConfidenceLevel] = useState("");
  const [materialTypes, setMaterialTypes] = useState([]);
  const [processFlows, setProcessFlows] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [campaignStatuses, setCampaignStatuses] = useState([]);
  const [changeControlStatuses, setChangeControlStatuses] = useState([]);
  const [deviationStatuses, setDeviationStatuses] = useState([]);
  const [inProcessTestStatuses, setInProcessTestStatuses] = useState([]);
  const [productReleaseTestStatuses, setProductReleaseTestStatuses] = useState([]);
  const [documentGroups, setDocumentGroups] = useState([]);
  const [settings, setSettings] = useState([
    {
      id: "01",
      name: "Fetch from CMO or Sponsor ERP system",
      selected: true,
      isNew: false,
    },
    {
      id: "02",
      name: "Fetch automatically from email",
      selected: true,
      isNew: false,
    },
    {
      id: "03",
      name: "Fetch automatically from the specified folder",
      selected: true,
      isNew: false,
    },
    {
      id: "04",
      name: "Ask user to manually upload the files",
      selected: true,
      isNew: false,
    },
  ]);

  // Use useMemo to always get the correct sponsor details
  const viewSponsorDetails = React.useMemo(() => {
    if (Array.isArray(reviewData?.SponsorDetails) && reviewData?.SponsorDetails.length > 0) {
      return reviewData.SponsorDetails[0];
    }
    return reviewData;
  }, [reviewData]);

  // Use useMemo for ecosystemURL and network
  const ecosystemURL = React.useMemo(() =>
    updateNetworkData?.ecosystemUrl ||
    rowData?.networkURL ||
    viewSponsorDetails?.networkURL,
    [updateNetworkData, rowData, viewSponsorDetails]
  );

  const network = React.useMemo(() =>
    addSponsorList?.data?.network ||
    rowNetwork ||
    viewSponsorDetails?.network,
    [addSponsorList, rowNetwork, viewSponsorDetails]
  );


  const OPEN_BACKDROP = "OPEN_BACKDROP";
  const DESTROY_BACKDROP = "DESTROY_BACKDROP";

  // ✅ Fetching from API
  const fetchProductModality = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchPlantSupplyTypes/false`;
      const response = await axios.get(url);

      if (response.data?.data?.status) {
        const list = response.data.data.supplyTypeList || [];
        const formatted = list.map((item, index) => ({
          ...item,
          id: item.supplyTypeID || `ID-${index + 1}`,
          name: item.supplyTypeName || "",
          selected: !!item.activated,
          isNew: false,
        }));

        setModalities(formatted);
      } else {
        setError("Something went wrong. Please try again.");
        console.error("Failed to fetch product types");
      }
    } catch (error) {
      setError("Something went wrong. Please try again.");
      console.error("Error fetching product types:", error);
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  useEffect(() => {
    if(network && ecosystemURL){
      fetchProductModality();
      fetchPlantTypes();
      fetchUOMs();
      fetchReasonCodes();
      fetchProductTypes();
      fetchFacilityTypes();
      fetchPoStatuses();
      fetchBatchStatuses();
      fetchShipmentStatuses();
      fetchDocumentTypes();
      fetchDocumentCategories();
      fetchFormatTypes();
      fetchDigitalizationCodes();
      fetchOrgLevelSettings();
      fetchMaterialTypes();
      fetchProcessFlows();
      fetchProjectStatuses();
      fetchDocumentGroups();
    }
  }, [network, ecosystemURL]);

  const fetchPlantTypes = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchPlantTypes/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (data?.status === true || data?.status === "true") {
        const list = data.plantTypeList || [];

        const formatted = list.map((item, index) => ({
          ...item,
          id: item.plantTypeID || `ID-${index + 1}`,
          name: item.plantTypeName || "",
          selected: !!item.activated,
          isNew: false,
        }));

        setPlantTypes(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  const fetchUOMs = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchAllUOMs/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      const list = data.productUOMsList || [];

      if (data?.status === true || data?.status === "true") {
        const formatted = list.map((item, index) => ({
          id: item.uomID || `ID-${index + 1}`,
          name: item.uom || "",
          selected: !!item.activated,
          isNew: false,
          uomType: item.uomType,
        }));
        setUoms(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  const fetchReasonCodes = async () => {
    setLoading(true);
    setError(null); // Reset error state before new fetch

    // Open backdrop before API call
    dispatch({
      type: OPEN_BACKDROP,
    });

    try {
      const requestObject = {
        searchField: "all",
        searchValue: "",
        reasonCodeGroupType: "all",
        reasonCodeType: "all",
        status: "all",
        collaboratorTypeID: "all",
      };

      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchReasonCodes`;

      const response = await axios.post(url, requestObject);
      if (response.data.status) {
        const result = response.data.data.reasonCodeList || [];
        setReasonCodes(result);
      }

      // Close backdrop after successful response
      dispatch({
        type: DESTROY_BACKDROP,
      });
    } catch (error) {
      setError("Something went wrong. Please try again.");

      // Close backdrop on error
      dispatch({
        type: DESTROY_BACKDROP,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProductTypes = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchProductTypes/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (data?.status === true || data?.status === "true") {
        const list = data.productTypeList || [];

        const formatted = list.map((item, index) => ({
          id: item.productTypeID || `ID-${index + 1}`,
          name: item.productTypeName || "",
          type: item.productType,
          selected: !!item.activated,
          isNew: false,
        }));

        setProductTypes(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  const fetchFacilityTypes = async () => {
    try {
      setLoading(true); // Show loading indicator
      setError(null); // Clear any previous errors

      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchFacilityTypes/false`;
      const response = await axios.get(url);

      const data = response?.data?.data;

      if (data?.status === true || data?.status === "true") {
        const list = data.facilityTypesList || [];

        const formatted = list.map((item, index) => ({
          id: item.facilityTypeID || `ID-${index + 1}`,
          name: item.facilityTypeName || "",
          type: item.facilityType || "default",
          selected: !!item.activated,
          associatedProductTypes: item.associatedProductTypes.map(
            (product) => ({
              id: product.productTypeID,
              name: product.productTypeName,
            })
          ),
          lastUpdatedBy: item.lastUpdatedBy || "",
          isNew: false,
        }));

        setFacilityTypes(formatted); // Update the state with the fetched facility types
      } else {
        setError("Failed to fetch facility types. Please try again.");
      }
    } catch (error) {
      setError(
        "An error occurred while fetching facility types. Please try again."
      );
    } finally {
      setLoading(false); // Hide loading indicator
    }
  };

  const fetchPoStatuses = async () => {
    setLoading(true); // Show loading spinner
    setError(""); // Reset error state
    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchFGStatuses/false`; // API endpoint
      const response = await axios.get(url);

      if (response.data?.status === true || response.data?.status === "true") {
        // Map the API response to match the expected structure
        const mappedStatuses = response.data?.data?.poStatuses?.map(
          (status) => ({
            id: status.status_id,
            name: status.status_name,
            selected: status.activated,
            type: status.status_type,
            isNew: false,
            newStatusName: status.newStatusName,
          })
        );

        setPoStatuses(mappedStatuses || []); // Update state with mapped statuses
      } else {
        console.error("Failed to fetch PO statuses:", response.data?.message);
        setError(response.data?.message || "Failed to fetch PO statuses.");
      }
    } catch (error) {
      console.error("Error fetching PO statuses:", error);
      setError("An error occurred while fetching PO statuses.");
    } finally {
      setLoading(false); // Hide loading spinner
    }
  };

  const fetchBatchStatuses = async () => {
    setError(null);
    setLoading(true);

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchFGStatuses/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        const list = data?.batchStatuses || [];

        // Map the API response to match the expected structure
        const formattedStatuses = list.map((item, index) => ({
          id: item.status_id || `ID-${index + 1}`,
          name: item.status_name || "",
          selected: !!item.activated,
          isNew: false,
          type: item.status_type,
          newName: item.newStatusName,
        }));

        setBatchStatuses(formattedStatuses);
      } else {
        console.error(
          "Failed to fetch batch statuses:",
          response.data?.message
        );
        setError(response.data?.message || "Failed to fetch batch statuses.");
      }
    } catch (error) {
      console.error("Error fetching batch statuses:", error);
      setError("An error occurred while fetching batch statuses.");
    } finally {
      setLoading(false);
    }
  };

  const fetchShipmentStatuses = async () => {
    setError(null); // Reset error state
    setLoading(true); // Show loading spinner

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchFGStatuses/false`; // API endpoint
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        const list = data?.shipmentStatuses || [];

        // Map the API response to match the expected structure
        const formattedStatuses = list.map((item, index) => ({
          id: item.status_id || `ID-${index + 1}`, // Use ID from API or generate one
          name: item.status_name || "", // Use name from API or fallback
          selected: !!item.activated, // Convert activation state to boolean
          type: item.status_type,
          isNew: false, // Default to false for fetched statuses
        }));

        setShipmentStatuses(formattedStatuses); // Update state with formatted statuses
      } else {
        console.error(
          "Failed to fetch shipment statuses:",
          response.data?.message
        );
        setError(
          response.data?.message || "Failed to fetch shipment statuses."
        );
      }
    } catch (error) {
      console.error("Error fetching shipment statuses:", error);
      setError("An error occurred while fetching shipment statuses.");
    } finally {
      setLoading(false); // Hide loading spinner
    }
  };

  const fetchDocumentTypes = async () => {
    setLoading(true); // Show the loading spinner
    setError(""); // Reset error state before making the API call
    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDocumentTypes/false`; // API endpoint
      const response = await axios.get(url);

      // Log full response for debugging

      if (response.data?.status === true || response.data?.status === "true") {
        // Check if documentTypeList exists and is an array
        const documentTypesList = response.data?.data?.documentTypesList || [];

        // Map the API response to match the expected structure
        const mappedDocumentTypes = documentTypesList.map((doc) => ({
          id: doc.documentTypeID, // Map documentTypeID to id with fallback
          name: doc.documentTypeName, // Map documentTypeName to name with fallback
          selected: !!doc.activated, // Map activated to selected, convert to boolean
          isNew: false,
          newDocumentTypeName: doc.newDocumentTypeName,
          type: doc.documentType,
          selectedCategory: doc.documentCategoryID || "", // Pre-populate category if exists

          // Default to false for fetched document types
        }));

        setDocumentTypes(mappedDocumentTypes); // Update state with mapped document types
      } else {
        console.error(
          "Failed to fetch document types:",
          response.data?.message
        );
        setError(
          response.data?.message || "Something went wrong. Please try again."
        ); // Set error message
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
      setError("Something went wrong. Please try again."); // Set error message
    } finally {
      setLoading(false); // Hide the loading spinner
    }
  };

  const fetchDocumentCategories = async () => {
    setLoading(true); // Show the loading spinner
    setError(""); // Reset error state before making the API call
    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDocumentCategories/false`; // API endpoint
      const response = await axios.get(url);

      if (response.data?.status === true || response.data?.status === "true") {
        // Check if documentCategoryList exists and is an array
        const documentCategoryList = response.data?.documentCategoryList || [];
        
        // Map the API response to match the expected structure
        const mappedDocumentCategories = documentCategoryList.map((doc) => ({
          id: doc.documentCategoryID, // Map documentCategoryID to id
          name: doc.documentCategoryName, // Map documentCategoryName to name
          selected: !!doc.activated, // Map activated to selected, convert to boolean
          isNew: false,
          type: doc.documentCategoryType, // Map documentCategoryType to type
          lastUpdatedBy: doc.lastUpdatedBy, // Keep lastUpdatedBy info
        }));

        setDocumentCategories(mappedDocumentCategories); // Update state with mapped document categories
      } else {
        console.error("Failed to fetch document categories:", response.data?.message);
        setError(response.data?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching document categories:", error);
      setError("Something went wrong. Please try again."); // Set error message
    } finally {
      setLoading(false); // Hide the loading spinner
    }
  };

  const fetchFormatTypes = async () => {
    setError(null);
    setLoading(true);
    dispatch({ type: "OPEN_BACKDROP" });

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchFileFormatTypes/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (data?.status === true || data?.status === "true") {
        const list = data.fileFormatTypesList || [];

        const formatted = list.map((item, index) => ({
          id: item.fileFormatTypeID || `ID-${index + 1}`,
          name: item.fileFormatTypeName || "",
          selected: !!item.activated,
          isNew: false,
          type: item.fileFormatType,
        }));

        setFormatTypes(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      dispatch({ type: "DESTROY_BACKDROP" });
    }
  };

  const fetchMaterialTypes = async () => {
    setError(null);
    setLoading(true);
    const cmoConfig = false
    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchMaterialTypes/${cmoConfig}`;
      const response = await axios.get(url);
      const data = response?.data;
      if (data?.status === true || data?.status === "true") {
        const list = data.materialTypeList || [];
        const formatted = list.map((item, index) => ({
          id: item.materialTypeID || `ID-${index + 1}`,
          name: item.materialTypeName || "",
          type: item.materialType,
          selected: !!item.activated,
          isNew: false,
        }));
        setMaterialTypes(formatted);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessFlows = async () => {
    setError(null);
    setLoading(true);
    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchAllProcessFlows/false`;
      const response = await axios.get(url);
      if (response.data && response.data.status && response.data.processFlowsList) {
        const transformedData = response.data.processFlowsList.map((item, index) => ({
          id: item.processFlowID?.toString() || `ID-${index + 1}`,
          name: item.processFlowName || '',
          mapping: "Click here to view",
          version: item.versionHistory && item.versionHistory.length > 0 ? "Click here to view" : "NA",
          qaCheck: item.qaCheckRequired ? "Yes" : "No",
          cmoCountry: item.cmoCountry || '',
          addresseeOrgName: item.addresseeOrgName || '',
          addresseeCountry: item.addresseeCountry || '',
          vendor: item.cmoCountry && item.addresseeOrgName && item.addresseeCountry
            ? [`${item.cmoCountry} - ${item.addresseeOrgName} - ${item.addresseeCountry}`]
            : [],
          selected: !!item.activated,
          isNew: false,
          processFlowType: item.processFlowType || 'Default',
          originalData: item,
        }));
        setProcessFlows(transformedData);
      }
    } catch (e) {
      setError("Error fetching process flows.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDigitalizationCodes = async () => {
    setError(null);
    setLoading(true);

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDigitalizationCodes`;
      const response = await axios.get(url);
      const data = response?.data;
      console.log(data);
      if (data?.status === true || data?.status === "true") {
        const list = data.digitalizationCodesList || [];
        const digitalizationCodesList = list.map((item, index) => ({
          id: item.digitalizationCodeID || `ID-${index + 1}`,
          code: item.digitalizationCode,
          description: item.digitalizationCodeDescription || "",
          selected: !!item.activated,
          isNew: false,
          type: item.digitalizationCodeType,
        }));
        setDigitalizationCodes(digitalizationCodesList);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgLevelSettings = async () => {
  try {
    const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchOrgLevelSettings`;
    const response = await axios.get(url);

    if (response.data?.orgLevelSettings) {
      const settings = response.data.orgLevelSettings;
      setLowConfidenceLevel(settings.lowConfidenceLimit || "");
      setMediumConfidenceLevel({
        min: settings.mediumConfidenceLimitMinValue || "",
        max: settings.mediumConfidenceLimitMaxValue || "",
      });
      setHighConfidenceLevel(settings.highConfidenceLimit || "");
    }
  } catch (err) {
    console.error("Error fetching Org Level Settings:", err);
  }
};

  const fetchProjectStatuses = async () => {
    setError(null);
    setLoading(true);

    try {
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchPDStatuses/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        // Format Project Statuses
        const projectList = data?.projectStatuses || [];
        const formattedProject = projectList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setProjectStatuses(formattedProject);

        // Format Campaign Statuses
        const campaignList = data?.campaignStatuses || [];
        const formattedCampaign = campaignList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setCampaignStatuses(formattedCampaign);

        // Format Change Control Statuses
        const changeControlList = data?.changeControlStatuses || [];
        const formattedChangeControl = changeControlList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setChangeControlStatuses(formattedChangeControl);

        // Format Deviation Statuses
        const deviationList = data?.deviationStatuses || [];
        const formattedDeviation = deviationList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setDeviationStatuses(formattedDeviation);

        // Format In-Process Test Statuses
        const inProcessTestList = data?.inProcessTestStatuses || [];
        const formattedInProcessTest = inProcessTestList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setInProcessTestStatuses(formattedInProcessTest);

        // Format Product Release Test Statuses
        const productReleaseTestList = data?.productReleaseTestStatuses || [];
        const formattedProductReleaseTest = productReleaseTestList.map((item, index) => ({
          id: item.statusID || `ID-${index + 1}`,
          name: item.statusName || "",
          selected: !!item.activated,
          type: item.statusType,
          isNew: false,
          newStatusName: item.newStatusName,
        }));
        setProductReleaseTestStatuses(formattedProductReleaseTest);
      } else {
        console.error("Failed to fetch project statuses:", response.data?.message);
        setError(response.data?.message || "Failed to fetch project statuses.");
      }
    } catch (error) {
      console.error("Error fetching project statuses:", error);
      setError("An error occurred while fetching project statuses.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentGroups = async () => {
    setError(null);
    setLoading(true);

    try {
      // projectConfig = false for sponsor network configurations UI
      const url = `${ecosystemURL}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDocumentGroups/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        const list = data?.documentGroups || [];
        const formattedGroups = list.map((item, index) => ({
          id: item.documentGroupID || `ID-${index + 1}`,
          name: item.documentGroupName || "",
          selected: !!item.activated,
          type: item.documentGroupType,
          isNew: false,
          lastUpdatedBy: item.lastUpdatedBy,
        }));

        setDocumentGroups(formattedGroups);
      } else {
        console.error("Failed to fetch document groups:", response.data?.message);
        setError(response.data?.message || "Failed to fetch document groups.");
      }
    } catch (error) {
      console.error("Error fetching document groups:", error);
      setError("An error occurred while fetching document groups.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* ********Manufacturing Supply Type******   */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>
              Manufacturing Supply Type
            </TableCell>
            <TableCell className={classes.headerCell}>
              Manufacturing Supply Type ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={6}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "50px",
                    border: "2px solid #FF0000",
                    backgroundColor: "#FFF0F0",
                    width: "100%",
                  }}
                >
                  <Typography style={{ textAlign: "center" }}>
                    {error}
                  </Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            modalities.map((modality, index) => (
              <TableRow key={modality.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={modality.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {modality.name}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {modality.id}
                </TableCell>
                {/* <TableCell className={classes.bodyCell}>
                  {modality.isNew === true ||
                  modality.supplyType !== "default" ? (
                    <IconButton
                      aria-label="delete"
                      onClick={() => deleteProductTypes(modality)}
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  ) : (
                    ""
                  )}
                </TableCell> */}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* ******Plant type********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Plant Type</TableCell>
            <TableCell className={classes.headerCell}>Plant Type ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            plantTypes.map((plantType, index) => (
              <TableRow key={plantType.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={plantType.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {plantType.name}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {plantType.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* **********uom************** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>UOM</TableCell>
            <TableCell className={classes.headerCell}>UOM ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            uoms.map((uom, index) => (
              <TableRow key={uom.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={uom.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{uom.name}</TableCell>
                <TableCell className={classes.bodyCell}>{uom.id}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* **********Reason Code******** */}
      {/* <TableContainer component={Paper} className="mb-4"> */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            {/* <TableCell className={classes.headerCell}>Selection</TableCell> */}
            <TableCell className={classes.headerCell}>Code Group</TableCell>
            <TableCell className={classes.headerCell}>
              Code Group Type
            </TableCell>
            <TableCell className={classes.headerCell}>Reason Code</TableCell>
            <TableCell className={classes.headerCell}>
              Reason Code Description
            </TableCell>
            <TableCell className={classes.headerCell}>
              {/* Reason Code Group  */}
              Reason Code Type
            </TableCell>
            {/* <TableCell className={classes.headerCell}>Reason Code Group ID</TableCell> */}
            <TableCell className={classes.headerCell}>
              {/* View Reason Codes  */}
              Visible To Organization
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reasonCodes.map((row) => (
            <TableRow key={row.reasonCode}>
              <TableCell className={classes.bodyCell}>
                {row.reasonCodeGroup}
              </TableCell>
              <TableCell>{row.reasonCodeGroupType}</TableCell>
              <TableCell className={classes.bodyCell}>
                {row.reasonCode}
              </TableCell>
              <TableCell className={classes.bodyCell}>
                {row.reasonDescription}
              </TableCell>
              <TableCell className={classes.bodyCell}>
                {/* {row.reasonCodeID} */}
                 {row.reasonCodeType}
              </TableCell>
              <TableCell className={classes.bodyCell}>
                 {row.Visibletoorg}
                {/* <Typography
                  style={{ color: "#6997dd", textDecoration: "underline" }}
                >
                  Click here to view
                </Typography> */}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* </TableContainer> */}

      {/* **************Material Types*********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Material Type</TableCell>
            <TableCell className={classes.headerCell}>Material Type ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            materialTypes.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell className={classes.bodyCell}>{item.id}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* **************Product type*********** */}

      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Product Type</TableCell>
            <TableCell className={classes.headerCell}>
              Product Type ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            productTypes.map(
              (item, index) =>
                ( // Debugging
                  <TableRow key={item.id}>
                    <TableCell className={classes.bodyCell}>
                      <Switch
                        checked={item.selected}
                        // onChange={() => handleToggle(index)}
                        classes={{ switchBase: classes.switchBase }}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {item.name}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {item.id}
                    </TableCell>
                  </TableRow>
                )
            )
          )}
        </TableBody>
      </Table>

      {/* ******** Manufacturing Facility Type******* */}

      <Table style={{ marginTop: "2rem" }} className={classes.tablefacility}>
        <TableHead style={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell className={classes.headerCell}>
              <b>Selection</b>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <b>Manufacturing Facility Type</b>
            </TableCell>
            <TableCell align="right" className={classes.headerCell}>
              <b>
                <span style={{ whiteSpace: "nowrap" }}>
                  Manufacturing Facility
                </span>{" "}
                <br /> Type ID
              </b>
            </TableCell>
            <TableCell
              style={{ whiteSpace: "nowrap", paddingLeft: "2rem" }}
              className={classes.headerCell}
            >
              <b>Associated Product Types</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={5}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            facilityTypes.map((item, index) => (
              <TableRow key={item.id || `new-${index}`}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    // onChange={() => handleToggle(index)}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {/* {item.isNew ? (
                      <TextField
                        value={item.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        placeholder="Enter Facility Type Name"
                        size="small"
                      />
                    ) : ( */}
                  {item.name}
                  {/* )} */}
                </TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
                <TableCell
                  style={{ whiteSpace: "nowrap", paddingLeft: "2rem" }}
                  className={classes.bodyCell}
                >
                  {/* {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Select
                          multiple
                          displayEmpty
                          value={
                            item.products
                              ? item.products.map((product) => product.id)
                              : []
                          } // Use IDs for the value
                          onChange={(e) => {
                            const selectedIds = e.target.value; // Array of selected IDs
                            const selectedProducts = productOptions.filter(
                              (option) => selectedIds.includes(option.id)
                            ); // Get the selected products (id and name)

                            setFacilityTypes((prev) =>
                              prev.map((row, i) =>
                                i === index
                                  ? { ...row, products: selectedProducts }
                                  : row
                              )
                            );
                            setIsUpdated(true);
                          }}
                          onOpen={fetchProductOptions} // Fetch product options when the dropdown is opened
                          renderValue={(selected) =>
                            selected.length > 0
                              ? selected
                                  .map(
                                    (id) =>
                                      productOptions.find(
                                        (option) => option.id === id
                                      )?.name
                                  )
                                  .join(", ")
                              : "Select from the list"
                          }
                          style={{ width: 200 }}
                          size="small"
                        >
                          {productOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              <Checkbox
                                checked={
                                  item.products
                                    ? item.products.some(
                                        (product) => product.id === option.id
                                      )
                                    : false
                                }
                              />
                              <ListItemText primary={option.name} />
                            </MenuItem>
                          ))}
                        </Select>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            onClick={() => handleSaveNewFacilityType(index)}
                            className={classes.iconBtn}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleCancel(index)}
                            className={classes.iconBtn}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : ( */}
                  {Array.isArray(item.associatedProductTypes) &&
                    item.associatedProductTypes.map((product, index) => (
                      <Typography key={product.id} component="span">
                        {product.name}
                        {index < item.associatedProductTypes.length - 1 && ", "}
                      </Typography>
                    ))}
                  {/* )} */}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* ************PO Status***********/}

      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>PO Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              PO Status ID
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : poStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No PO Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            poStatuses.map((status, index) => (
              <TableRow key={status.id || index}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={status.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{
                      switchBase: classes.switchBase,
                    }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {/* {status.isNew ? (
                      <div
                        style={{ display: "flex", alignItems: "flex-start" }}
                      >
                        <TextField
                          className={classes.textField}
                          placeholder="Type Text Here"
                          value={status.name}
                          onChange={(e) =>
                            handleNameChange(index, e.target.value)
                          }
                        />
                        <div className={classes.inlineActions}>
                          <IconButton
                            onClick={() => saveNewPo(index)}
                            className={classes.iconBtn}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleCancel(index)}
                            className={classes.iconBtn}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : ( */}
                  {status.name}
                  {/* )} */}
                </TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {status.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* ***************batch Status************ */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Batch Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Batch Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            // Show loading spinner when loading is true
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            // Show error message when error exists
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : batchStatuses.length === 0 ? (
            // Show "No Data" message when batchStatuses is empty
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Batch Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Render batch statuses when available
            batchStatuses.map((status, index) => (
              <TableRow key={status.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={status.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {status.name}
                </TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {status.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Shipment Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>
              Shipment Status
            </TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Shipment Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            // Show loading spinner when loading is true
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            // Show error message when error exists
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : shipmentStatuses.length === 0 ? (
            // Show "No Data" message when batchStatuses is empty
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Batch Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Render shipmentStatuses data when available
            shipmentStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Document Group********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Document Group</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Document Group ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : documentGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Document Groups Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            documentGroups.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Project Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Project Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Project Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : projectStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Project Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            projectStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Campaign Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Campaign Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Campaign Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : campaignStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Campaign Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            campaignStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Change Control Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Change Control Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Change Control Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : changeControlStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Change Control Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            changeControlStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Deviation Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Deviation Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Deviation Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : deviationStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Deviation Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            deviationStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************In-Process Test Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>In-Process Test Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              In-Process Test Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : inProcessTestStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No In-Process Test Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            inProcessTestStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *************Product Release Test Status********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Product Release Test Status</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Product Release Test Status ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : productReleaseTestStatuses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Product Release Test Statuses Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            productReleaseTestStatuses.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* **********Document Categories********* */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Document Category</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Document Category ID
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            // Show loading spinner when loading is true
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            // Show error message when error exists
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : documentCategories.length === 0 ? (
            // Show "No Data" message when documentCategories is empty
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Document Categories Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Render document categories when data is available
            documentCategories.map((category, index) => (
              <TableRow key={category.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={category.selected} // Bind to the selected state
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {category.name} 
                </TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {category.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* **********Document Type********* */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Document Type</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Document Type ID
            </TableCell>
            <TableCell className={classes.headerCell}>Document Category</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            // Show loading spinner when loading is true
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            // Show error message when error exists
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : documentTypes.length === 0 ? (
            // Show "No Data" message when documentTypes is empty
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Document Types Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Render document types when data is available
            documentTypes.map((doc, index) => (
              <TableRow key={doc.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={doc.selected} // Bind to the selected state
                    // onChange={() => handleToggle(index)} // Handle toggle changes
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{doc.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {doc.id}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {doc.selectedCategory ? (
                    // Find and display the category name
                    documentCategories.find(cat => cat.id == doc.selectedCategory)?.name || 
                    <span style={{ color: "#999" }}>
                      {/* Category not found */}
                    </span>
                  ) : (
                    <span style={{ color: "#999" }}>
                      {/* No category assigned */}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* *********Format Type********** */}
      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Format Type</TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Format Type ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            formatTypes.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell align="right" className={classes.bodyCell}>
                  {item.id}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* --- Add Process Flow Section --- */}
      <Table style={{ marginTop: '2rem' }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Process Flow</TableCell>
            <TableCell className={classes.headerCell}>Process Flow ID</TableCell>
            <TableCell className={classes.headerCell}>QA Check Req.</TableCell>
            <TableCell className={classes.headerCell}>CMO Country</TableCell>
            <TableCell className={classes.headerCell}>Addressee Org Name</TableCell>
            <TableCell className={classes.headerCell}>Addressee Country</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={7}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            processFlows.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    // onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.name}</TableCell>
                <TableCell className={classes.bodyCell}>{item.id}</TableCell>
                <TableCell className={classes.bodyCell}>{item.qaCheck}</TableCell>
                <TableCell className={classes.bodyCell}>{item.cmoCountry}</TableCell>
                <TableCell className={classes.bodyCell}>{item.addresseeOrgName}</TableCell>
                <TableCell className={classes.bodyCell}>{item.addresseeCountry}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Digitalization Code</TableCell>
            <TableCell className={classes.headerCell}>
              Digitalization Code Description
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            digitalizationCodes.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>{item.code}</TableCell>
                <TableCell align="left" className={classes.bodyCell}>
                  {item.description}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className={classes.confidenceSection}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceHeading}>
              Low Confidence Level (&lt;/=) %
            </Typography>
            <Typography variant="body1" className={classes.confidenceValue}>
              {lowConfidenceLevel || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceHeading}>
              Medium Confidence Level (between) %
            </Typography>
            <Typography variant="body1" className={classes.confidenceValue}>
              {mediumConfidenceLevel.min || "-"}&nbsp;to&nbsp;
              {mediumConfidenceLevel.max || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography className={classes.confidenceHeading}>
              High Confidence Level (&gt;/=) %
            </Typography>
            <Typography variant="body1" className={classes.confidenceValue}>
              {highConfidenceLevel || "-"}
            </Typography>
          </Grid>
        </Grid>
      </div>

      {/* **************Upload Fetch String******** */}
      {/* <Table style={{ marginTop: "2rem" }} className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>
              Upload/Fetch Setting
            </TableCell>
            <TableCell align="right" className={classes.headerCell}>
              Upload/Fetch Setting ID
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {settings.map((item, index) => (
            <TableRow key={item.id}>
              <TableCell className={classes.bodyCell}>
                <Switch
                  checked={item.selected}
                  // onChange={() => handleToggle(index)}
                  classes={{ switchBase: classes.switchBase }}
                />
              </TableCell>
              <TableCell className={classes.bodyCell}>{item.name}</TableCell>
              <TableCell align="right" className={classes.bodyCell}>
                {item.id}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table> */}
    </div>
  );
};

export default SponsorNetworkList;
